'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface EmergencyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EmergencyModal({ isOpen, onClose }: EmergencyModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card rounded-lg shadow-elevated max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-error px-6 py-4 flex items-center justify-between border-b border-error/20">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Icon name="ExclamationTriangleIcon" size={24} className="text-white" variant="solid" />
            </div>
            <div>
              <h3 className="font-headline font-bold text-xl text-white">
                Emergency IT Support
              </h3>
              <p className="font-body text-sm text-white/90">
                24/7 Critical Response Team
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors duration-200"
          >
            <Icon name="XMarkIcon" size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Urgency Notice */}
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <p className="font-body text-sm text-foreground">
              <strong>Emergency support is for critical issues only:</strong> Server outages, security breaches, data loss, network failures, or any situation causing immediate business disruption.
            </p>
          </div>

          {/* Contact Methods */}
          <div className="space-y-4">
            <h4 className="font-headline font-bold text-lg text-primary">
              Immediate Contact Options
            </h4>

            {/* Phone */}
            <div className="bg-muted rounded-lg p-4 hover:shadow-card transition-all duration-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-error rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="PhoneIcon" size={24} className="text-white" variant="solid" />
                </div>
                <div className="flex-grow">
                  <h5 className="font-headline font-bold text-base text-primary mb-1">
                    Emergency Hotline
                  </h5>
                  <p className="font-body text-sm text-muted-foreground mb-2">
                    Speak directly with our on-call technician
                  </p>
                  <a
                    href="tel:+966552258277"
                    className="inline-flex items-center space-x-2 font-cta font-semibold text-error hover:text-error/80 transition-colors duration-200"
                  >
                    <span>+966 55 2258277</span>
                    <Icon name="ArrowRightIcon" size={16} />
                  </a>
                  <div className="flex items-center space-x-2 mt-2">
                    <div className="w-2 h-2 bg-conversion-accent rounded-full animate-pulse" />
                    <span className="text-xs font-body text-muted-foreground">Average response: &lt;5 minutes</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Priority Chat */}
            <div className="bg-muted rounded-lg p-4 hover:shadow-card transition-all duration-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-brand-primary rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-white" variant="solid" />
                </div>
                <div className="flex-grow">
                  <h5 className="font-headline font-bold text-base text-primary mb-1">
                    Priority Live Chat
                  </h5>
                  <p className="font-body text-sm text-muted-foreground mb-2">
                    Connect instantly with emergency support team
                  </p>
                  <button className="inline-flex items-center space-x-2 font-cta font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors duration-200">
                    <span>Start Emergency Chat</span>
                    <Icon name="ArrowRightIcon" size={16} />
                  </button>
                  <div className="flex items-center space-x-2 mt-2">
                    <div className="w-2 h-2 bg-conversion-accent rounded-full animate-pulse" />
                    <span className="text-xs font-body text-muted-foreground">Average response: &lt;2 minutes</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Email (Last Resort) */}
            <div className="bg-muted rounded-lg p-4 hover:shadow-card transition-all duration-200">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-brand-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="EnvelopeIcon" size={24} className="text-white" variant="solid" />
                </div>
                <div className="flex-grow">
                  <h5 className="font-headline font-bold text-base text-primary mb-1">
                    Emergency Email
                  </h5>
                  <p className="font-body text-sm text-muted-foreground mb-2">
                    For urgent issues when phone/chat unavailable
                  </p>
                  <a
                    href="mailto:info@centronics.com.sa"
                    className="inline-flex items-center space-x-2 font-cta font-semibold text-brand-secondary hover:text-brand-secondary/80 transition-colors duration-200"
                  >
                    <span>info@centronics.com.sa</span>
                    <Icon name="ArrowRightIcon" size={16} />
                  </a>
                  <div className="flex items-center space-x-2 mt-2">
                    <Icon name="ClockIcon" size={14} className="text-muted-foreground" />
                    <span className="text-xs font-body text-muted-foreground">Response within 30 minutes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* What to Prepare */}
          <div className="bg-muted rounded-lg p-4">
            <h4 className="font-headline font-bold text-base text-primary mb-3 flex items-center space-x-2">
              <Icon name="InformationCircleIcon" size={20} className="text-brand-primary" />
              <span>Have This Information Ready</span>
            </h4>
            <ul className="space-y-2">
              {[
                'Your company name and contact information',
                'Description of the critical issue',
                'Systems or services affected',
                'When the problem started',
                'Any error messages or codes',
                'Steps already taken to resolve'
              ].map((item, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={16} className="text-conversion-accent mt-0.5 flex-shrink-0" variant="solid" />
                  <span className="font-body text-sm text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* SLA Guarantee */}
          <div className="bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 border border-brand-primary/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="ShieldCheckIcon" size={24} className="text-brand-primary flex-shrink-0" variant="solid" />
              <div>
                <h5 className="font-headline font-bold text-base text-primary mb-1">
                  Our Emergency Response Guarantee
                </h5>
                <p className="font-body text-sm text-muted-foreground">
                  We guarantee initial response within 15 minutes for all emergency support requests. Our certified technicians are available 24/7/365 to resolve critical issues and minimize downtime.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-muted px-6 py-4 border-t border-border">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-card hover:bg-background border border-border text-foreground rounded-lg font-cta font-semibold text-sm transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}